/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnInit} from '@angular/core';
import {completeCopyModal} from '../common/util/complete-copy-modal';
import {Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {nextCopyTitle} from '../../components/util/next-copy-title';
import {Uuid} from '../../components/util/uuid';
import {AdnModalLoadingService} from '../common/util/adn-modal-loading.service';
import {CreativeSetApiService} from './creative-set-api.service';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';

export interface CreativeSetCopyResult {
  model: {id: string};
  destination: boolean;
}

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-creative-set-copy-modal',
  template: `
    <form class="form-horizontal" (ngSubmit)="copy()">
      <div class="modal-header dialogAddStuff">
        <button type="button" class="close" (click)="dismiss()">&times;</button>
        <h4 class="modal-title">
          Creative Set
          <small>{{ originalName }}</small>
        </h4>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label class="col-md-3 control-label">Name</label>
          <div class="col-md-9">
            <input type="text" class="form-control" name="creativeSet.name" [(ngModel)]="name" required>
          </div>
        </div>
        <div class="form-group">
          <label class="control-label col-md-3">State</label>
          <div class="col-md-9">
            <div class="btn-group">
              <label class="btn btn-default btn-xs" name="copy.userState.incomplete" [(ngModel)]="userState" btnRadio="INCOMPLETE">Incomplete</label>
              <label class="btn btn-default btn-xs" name="copy.userState.approved" [(ngModel)]="userState" btnRadio="APPROVED">Approved</label>
              <label class="btn btn-default btn-xs" name="copy.userState.paused" [(ngModel)]="userState" btnRadio="PAUSED">Paused</label>
            </div>
          </div>
        </div>
        <div class="form-group">
          <label class="control-label col-md-3">Include in Copy</label>
          <div class="col-md-9">
            <div class="checkbox">
              <label>
                <input type="checkbox" [(ngModel)]="copyCreatives" name="copy.creatives">
                <span>{{ 'creative.plural' | ogTranslate }}</span>
              </label>
            </div>
          </div>
        </div>
        <div class="checkbox text-right">
          <label class="checkbox">
            <input type="checkbox" [(ngModel)]="destination" name="copy.destination">
            <span class="small">Go to new Creative Set after copy</span>
          </label>
        </div>
      </div>
      <div class="modal-footer">
        <a class="btn btn-link btn-xs" (click)="dismiss()">{{ 'button.cancel' | ogTranslate }}</a>
        <button type="submit" class="btn btn-default" [disabled]="disabled">{{ 'label.copy' | ogTranslate }}</button>
      </div>
    </form>
  `
})
export class CreativeSetCopyModalComponent implements OnInit {
  model: any;

  readonly onCopied = new Subject<CreativeSetCopyResult>();

  originalName = '';
  name = '';
  userState = 'APPROVED';
  copyCreatives = true;
  destination = true;
  disabled = false;

  constructor(
    public bsModalRef: BsModalRef,
    private readonly creativeSetApi: CreativeSetApiService,
    @Inject(AdnModalLoadingService) private readonly adnModalLoading: any
  ) {
  }

  ngOnInit(): void {
    this.originalName = this.model?.name || '';
    this.name = nextCopyTitle(this.model?.name || '');
    this.userState = this.model?.userState || 'APPROVED';
  }

  copy(): void {
    this.disabled = true;
    this.adnModalLoading.applyModalLoading();
    const newId = Uuid.generate();
    this.creativeSetApi.copy(this.model.id, newId, {
      name: this.name,
      userState: this.userState,
      copyCreatives: this.copyCreatives
    }).subscribe({
      next: () => {
        completeCopyModal(this.bsModalRef, this.onCopied, {model: {id: newId}, destination: this.destination});
        this.adnModalLoading.removeModalLoading();
      },
      error: () => {
        this.disabled = false;
        this.adnModalLoading.removeModalLoading();
      }
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }
}
